import { apiClient } from '../api/fetchClient';
import type { NeedAccessReq, NeedAccessResponse } from '../need-access/need-access.interface';
import type {
  AccessResponse,
  VideoAccessResponse,
  VideoListAccessResponse,
} from './models/access-response';
import type { CollectiblesResponse } from './models/collectibles.interface';
import type {
  PromotedProductsReq,
  PromotedProductsResponse,
} from './models/promoted-products.interface';
import type { VideoBarRequest } from './models/video-bar.interface';

export class ContentService {
  async loadVideo(id: string): Promise<VideoAccessResponse> {
    const url = new URL(`/apps/single/v3/content/video/${id}`, window.location.origin);

    return await apiClient.get<VideoAccessResponse>(url.pathname + url.search);
  }

  async loadAudioPlayback(id: string): Promise<{ playbackId?: string; token?: string | null }> {
    const url = new URL(`/apps/single/v3/content/audio/${id}`, window.location.origin);

    // The endpoint returns a ContentAccessResponse envelope; the perk lives in .content
    const response = await apiClient.get<{
      content?: { playbackId?: string; token?: string | null };
    }>(url.pathname + url.search);
    return { playbackId: response?.content?.playbackId, token: response?.content?.token };
  }

  async loadVideoBar(request: VideoBarRequest): Promise<VideoListAccessResponse> {
    const url = new URL('/apps/single/v3/content/video-bar', window.location.origin);
    if (request.query) {
      url.searchParams.set('query', request.query);
    } else {
      url.searchParams.delete('query');
    }

    return await apiClient.post<VideoListAccessResponse>(url.pathname + url.search, request);
  }

  async loadProductAccess(id: string): Promise<AccessResponse> {
    const url = new URL(`/apps/single/v3/product/access/${id}`, window.location.origin);
    return await apiClient.get<AccessResponse>(url.pathname + url.search);
  }

  async loadNeedAccess(request: NeedAccessReq): Promise<NeedAccessResponse> {
    const url = new URL(`/apps/single/v3/content/need-access`, window.location.origin);

    return await apiClient.post<NeedAccessResponse>(url.pathname + url.search, request);
  }

  async loadPromotedProducts(request: PromotedProductsReq): Promise<PromotedProductsResponse> {
    const url = new URL(`/apps/single/v3/content/promoted-products`, window.location.origin);

    return await apiClient.post<PromotedProductsResponse>(url.pathname + url.search, request);
  }

  async loadCollectibles(): Promise<CollectiblesResponse> {
    const url = new URL(`/apps/single/v3/collectibles`, window.location.origin);
    return await apiClient.get(url.pathname + url.search);
  }
}
